import React, { useEffect, useMemo, useRef } from "react";
import { Chart, registerables } from "chart.js";
import styles from "./HealingTrajectoryChart.module.scss";

Chart.register(...registerables);

const mcsdBandPlugin = {
  id: "mcsdBand",
  beforeDatasetsDraw(chart, _args, opts) {
    const { bandYMin, bandYMax } = opts;
    if (bandYMin == null || bandYMax == null) return;

    const {
      ctx,
      chartArea: { left, right },
      scales,
    } = chart;
    const yS = scales.y;

    const pxTop = yS.getPixelForValue(Math.max(bandYMin, bandYMax));
    const pxBottom = yS.getPixelForValue(Math.min(bandYMin, bandYMax));

    ctx.save();

    ctx.fillStyle = "rgba(234,179,8,0.06)";
    ctx.fillRect(left, pxTop, right - left, pxBottom - pxTop);

    ctx.beginPath();
    ctx.moveTo(left, pxTop);
    ctx.lineTo(right, pxTop);
    ctx.strokeStyle = "rgba(234,179,8,0.3)";
    ctx.lineWidth = 1;
    ctx.setLineDash([5, 4]);
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(left, pxBottom);
    ctx.lineTo(right, pxBottom);
    ctx.stroke();

    ctx.setLineDash([]);
    ctx.restore();
  },
};

// ─── Plugin: value label above dot + % badge below dot ───────────────────────

const valueLabelsPlugin = {
  id: "valueLabels",
  afterDatasetsDraw(chart, _args, opts) {
    const { ctx, scales } = chart;
    const xS = scales.x;
    const yS = scales.y;
    const rows = opts.rows || [];

    ctx.save();
    rows.forEach((row, i) => {
      const x = xS.getPixelForValue(i);
      const y = yS.getPixelForValue(row.y);

      ctx.font = "600 11px 'Segoe UI', sans-serif";
      ctx.textAlign = "center";
      ctx.fillStyle = "#333";
      ctx.fillText(Number(row.y).toFixed(1), x, y - 14);

      if (row.isBaseline) {
        ctx.font = "600 10px 'Segoe UI', sans-serif";
        ctx.fillStyle = "#9a7500";
        // ctx.fillText("Stable", x, y + 22);
      } else if (row.pct != null) {
        ctx.font = "600 10px 'Segoe UI', sans-serif";
        ctx.fillStyle = row.pct <= 0 ? "#2e7d32" : "#c62828";
        // ctx.fillText((row.pct > 0 ? "+" : "") + Number(row.pct).toFixed(1) + "%", x, y + 22);
      }
    });
    ctx.restore();
  },
};

// ─── Component ───────────────────────────────────────────────────────────────

const HealingTrajectoryChart = ({ data: dataProp, currPoint }) => {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);

  const rows = useMemo(() => {
    const source = dataProp ?? [
      { x: "0", y: "4.2", trend: "4", mscd: "3.4" },
      { x: "1", y: "3.7" },
      { x: "2", y: "3.4" },
      { x: "3", y: "3.6" },
      { x: "4", y: "3.1" },
      { x: "5", y: "3.4", trend: "3.2", mscd: "4.7" },
    ];

    const baseline = Number(source[0]?.y ?? 0);
    return source.map((d, i) => ({
      label: d.x,
      y: Number(d.y),
      trend: d.trend != null ? Number(d.trend) : null,
      mscd: d.mscd != null ? Number(d.mscd) : null,
      isBaseline: i === 0,
      pct: i === 0 ? null : +(((Number(d.y) - baseline) / baseline) * 100).toFixed(1),
    }));
  }, [dataProp]);

  const mcsdEntries = rows.filter((d) => d.mscd != null);
  const mcsdY1 = mcsdEntries.length > 0 ? Number(mcsdEntries[0].mscd) : null;
  const mcsdY2 = mcsdEntries.length > 1 ? Number(mcsdEntries[mcsdEntries.length - 1].mscd) : mcsdY1;

  const mcsdBandYMin = Math.min(mcsdY1, mcsdY2);
  const mcsdBandYMax = Math.max(mcsdY1, mcsdY2);

  const allYValues = [...rows.map((r) => r.y), ...rows.map((r) => r.trend).filter((v) => v != null)];
  const dataYMax = Math.max(...allYValues);
  const dataYMin = Math.min(...allYValues);
  const yAxisMax = Math.ceil(dataYMax + 0.5);
  const yAxisMin = Math.max(0, Math.floor(dataYMin - 0.5));

  useEffect(() => {
    if (!canvasRef.current) return;

    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }

    const dotFill = rows.map((r) => (r.isBaseline ? "#387ac4" : r.pct <= 0 ? "#4caf50" : "#e53935"));
    const dotBorder = rows.map((r) => (r.isBaseline ? "rgba(56,122,196,0.35)" : r.pct <= 0 ? "rgba(76,175,80,0.35)" : "rgba(229,57,53,0.35)"));

    chartRef.current = new Chart(canvasRef.current, {
      type: "line",
      plugins: [mcsdBandPlugin, valueLabelsPlugin],
      data: {
        labels: rows.map((r) => r.label),
        datasets: [
          {
            label: "Surface Area",
            data: rows.map((r) => r.y),
            borderColor: "#387ac4",
            borderWidth: 1.5,
            pointBackgroundColor: dotFill,
            pointBorderColor: dotBorder,
            pointBorderWidth: 4,
            pointRadius: 6,
            pointHoverRadius: 8,
            tension: 0,
          },
          {
            label: "Trend",
            data: rows.map((r) => r.trend),
            borderColor: "#ff7300",
            borderWidth: 1.5,
            borderDash: [5, 5],
            pointRadius: 0,
            tension: 0,
            spanGaps: true,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: { top: 30, bottom: 20, left: 10, right: 20 },
        },
        animation: { duration: 700, easing: "easeInOutQuart" },
        plugins: {
          mcsdBand: { bandYMin: mcsdBandYMin, bandYMax: mcsdBandYMax },
          valueLabels: { rows },
          legend: { display: false },
          tooltip: {
            backgroundColor: "#fff",
            borderColor: "#e0e0e0",
            borderWidth: 1,
            titleColor: "#555",
            bodyColor: "#222",
            padding: 10,
            callbacks: {
              label: (ctx) => {
                if (ctx.parsed.y == null) return null;
                const row = rows[ctx.dataIndex];
                if (ctx.datasetIndex === 0) {
                  return `Area: ${ctx.parsed.y} cm²`;
                  // return row.isBaseline ? `Area: ${ctx.parsed.y} cm²  · Stable` : `Area: ${ctx.parsed.y} cm²  (${row.pct > 0 ? "+" : ""}${row.pct}%)`;
                }
                return `Trend: ${ctx.parsed.y} cm²`;
              },
            },
          },
        },
        scales: {
          x: {
            grid: { color: "#e8e8e8", lineWidth: 1 },
            ticks: { color: "#888", font: { size: 11 }, maxRotation: 0 },
            title: { display: true, text: "Date", color: "#666", font: { size: 13 } },
            border: { color: "#ccc" },
          },
          y: {
            min: yAxisMin,
            max: yAxisMax,
            grid: { color: "#e8e8e8", lineWidth: 1 },
            ticks: { color: "#888", font: { size: 11 }, stepSize: 1 },
            title: {
              display: true,
              text: "Surface Area (cm²)",
              color: "#666",
              font: { size: 13 },
            },
            border: { color: "#ccc" },
          },
        },
      },
    });

    return () => {
      chartRef.current?.destroy();
      chartRef.current = null;
    };
  }, [rows, mcsdBandYMin, mcsdBandYMax, yAxisMin, yAxisMax]);

  return (
    <div className={styles.chartWrapper}>
      <p className={styles.chartTitle}>Wound Healing Trajectory</p>

      <div className={styles.legendRow}>
        <span className={styles.legendItem}>
          <span className={styles.legendLineSolid} />
          Actual
        </span>
        <span className={styles.legendItem}>
          <span className={styles.legendLineDash} />
          Trend
        </span>
        <span className={styles.legendItem}>
          <span className={styles.legendBandMcsd} />
          MCSD Zone
        </span>
      </div>

      <div className={styles.canvasContainer}>
        <canvas ref={canvasRef} role="img" aria-label="Wound surface area healing trajectory over time" />
      </div>
    </div>
  );
};

export default HealingTrajectoryChart;
